import { FC, useState } from 'react'
import {
  ArrowPathIcon,
  ChartBarIcon,
  CodeBracketSquareIcon,
  PlayCircleIcon,
  PlayIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline'
import {
  CmdCtrl,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Kbd,
  KbdGroup,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  ToggleGroup,
  ToggleGroupItem,
} from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { QueryHistory } from '@/components/QueryHistory/QueryHistory'
import { trackSql, type SqlAnalyticsEvent } from '@/modules/sqlAnalytics'

export type SqlTabToolbarHandlers = {
  run?: () => void
  runPartial?: () => void
  history?: () => void
  addToSolutionQuery?: () => void
  addToSolutionVisualization?: () => void
}

export interface SqlTabToolbarProps {
  handlers: SqlTabToolbarHandlers
  isRunning?: boolean
  hasSelection?: boolean
  hasVisualizationData?: boolean
  /** Challenge id for showing query history in the toolbar dropdown. */
  challengeId: string
  /** Optional handler when selecting a query from history. */
  onHistoryQuerySelect?: (query: string) => void
  isSchemaOpen?: boolean
  onSchemaToggle?: () => void
}

export const SqlTabToolbar: FC<SqlTabToolbarProps> = ({
  handlers,
  isRunning = false,
  hasSelection = false,
  hasVisualizationData = false,
  challengeId,
  onHistoryQuerySelect,
  isSchemaOpen = false,
  onSchemaToggle,
}) => {
  const h = { ...handlers }
  const { t } = useTar()
  const [runGroupValue, setRunGroupValue] = useState<string>('')
  const [addToSolutionValue, setAddToSolutionValue] = useState<string>('')
  const [recentValue, setRecentValue] = useState<string>('')
  const [openToolsDropdown, setOpenToolsDropdown] = useState<'history' | null>(null)

  return (
    <div className="hidden md:flex items-center gap-1 shrink-0 py-2 px-2 bg-card border-b border-border">
      <div className="flex flex-col gap-1 justify-end">
        <span className="text-xs font-medium text-muted-foreground px-1">
          {t('common.sqlToolbarExecuteGroup')}
        </span>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={runGroupValue}
          onValueChange={(v) => {
            setRunGroupValue('')
            const runHandlers: Record<string, () => void> = {
              run: () => h.run?.(),
              'run-partial': () => h.runPartial?.(),
            }
            runHandlers[v]?.()
            if (v === 'run' || v === 'run-partial') {
              trackSql('sql:query:run', {
                challengeId,
                method: 'button',
                isPartial: v === 'run-partial',
              })
            }
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <ToggleGroupItem
                value="run"
                aria-label={t('common.run')}
                disabled={isRunning}
                className="gap-1.5 whitespace-nowrap"
              >
                <PlayIcon className="w-4 h-4 shrink-0 text-red-800" />
                <span>{t('common.run')}</span>
              </ToggleGroupItem>
            </TooltipTrigger>
            <TooltipContent>
              <KbdGroup>
                <CmdCtrl />
                <Kbd>Enter</Kbd>
              </KbdGroup>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <ToggleGroupItem
                  value="run-partial"
                  aria-label={t('common.runPartial')}
                  disabled={isRunning || !hasSelection}
                  className="gap-1.5 whitespace-nowrap rounded-s-none border-s-0"
                >
                  <PlayCircleIcon className="w-4 h-4 shrink-0 text-amber-700" />
                  <span>{t('common.runPartial')}</span>
                </ToggleGroupItem>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <KbdGroup>
                <span className="text-xs font-medium text-muted-foreground me-1">Selection + </span>
                <CmdCtrl />
                <Kbd>Enter</Kbd>
              </KbdGroup>
            </TooltipContent>
          </Tooltip>
        </ToggleGroup>
      </div>
      <Separator orientation="vertical" className="h-7 mt-5 mx-1 self-stretch" />
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground px-1">
          {t('common.sqlToolbarAddToSolutionGroup')}
        </span>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={addToSolutionValue}
          onValueChange={(v) => {
            setAddToSolutionValue('')
            const addHandlers: Record<string, () => void> = {
              query: () => h.addToSolutionQuery?.(),
              visualization: () => h.addToSolutionVisualization?.(),
            }
            addHandlers[v]?.()
            const eventMap: Record<string, SqlAnalyticsEvent> = {
              query: 'sql:toolbar:add_query_to_solution',
              visualization: 'sql:toolbar:add_viz_to_solution',
            }
            if (eventMap[v]) trackSql(eventMap[v], { challengeId })
          }}
        >
          <ToggleGroupItem
            value="query"
            aria-label={t('challenges.query')}
            className="gap-1.5 whitespace-nowrap"
          >
            <CodeBracketSquareIcon className="w-4 h-4 shrink-0 text-emerald-900" />
            <span>{t('challenges.query')}</span>
          </ToggleGroupItem>
          <ToggleGroupItem
            value="visualization"
            aria-label={t('common.visualization')}
            disabled={!hasVisualizationData}
            className="gap-1.5 whitespace-nowrap"
          >
            <ChartBarIcon className="w-4 h-4 shrink-0 text-violet-800" />
            <span>{t('common.visualization')}</span>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <Separator orientation="vertical" className="h-7 mt-5 mx-1 self-stretch" />
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground px-1">
          {t('common.tools')}
        </span>
        <div className="flex">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={recentValue}
            onValueChange={(v) => {
              setRecentValue('')
              const toolsHandlers: Record<string, () => void> = {
                history: () => h.history?.(),
              }
              toolsHandlers[v]?.()
            }}
          >
            <DropdownMenu
              open={openToolsDropdown === 'history'}
              onOpenChange={(open) => {
                setOpenToolsDropdown(open ? 'history' : null)
                if (open) trackSql('sql:toolbar:history_open', { challengeId })
              }}
            >
              <DropdownMenuTrigger asChild>
                <ToggleGroupItem
                  value="history"
                  aria-label={t('common.history')}
                  className="gap-1.5 whitespace-nowrap rounded-e-none"
                >
                  <ArrowPathIcon className="w-4 h-4 shrink-0 text-sky-700" />
                  <span>{t('common.history')}</span>
                </ToggleGroupItem>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="p-0 w-[360px] max-h-[320px] custom-scrollbar"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <QueryHistory
                  challengeId={challengeId}
                  onQuerySelect={onHistoryQuerySelect}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </ToggleGroup>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={isSchemaOpen ? 'schema' : ''}
            onValueChange={() => onSchemaToggle?.()}
          >
            <ToggleGroupItem
              value="schema"
              aria-label={t('challenges.schema')}
              className="gap-1.5 whitespace-nowrap rounded-s-none border-s-0 data-[state=on]:bg-transparent data-[state=on]:text-inherit data-[state=on]:border-2 data-[state=on]:border-teal-700"
            >
              <TableCellsIcon className="w-4 h-4 shrink-0 text-teal-700" />
              <span>{t('challenges.schema')}</span>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
    </div>
  )
}
